/* oxlint-disable react/no-array-index-key */
import { Checkbox } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/sharp-regular-svg-icons";

import { ChangeEventHandler, useEffect, useRef, useState } from "react";
import styles from "./provider-baa-form.module.scss";
import { BAA_COPY, TOS_LINK_TEXT } from "./baaCopy";
import { interleave } from "../../utils/interleave";

const createBaaLineElements = (text: string) => {
  const lines = text.split("\n");

  return lines.flatMap((line, row) => {
    if (!line.trim().length) {
      return <br key={row} />;
    }

    const lineElements = line.split("  ").flatMap((lineElement) => {
      if (lineElement.includes(TOS_LINK_TEXT)) {
        return interleave(
          lineElement
            .split(TOS_LINK_TEXT)
            .map((remainingLineElement) => <span>{remainingLineElement}</span>),
          <a href="http://www.getclaimable.com/partner-terms-of-service">
            {TOS_LINK_TEXT}
          </a>,
        );
      }

      return <span>{lineElement}</span>;
    });

    return [...interleave(lineElements, <span>&nbsp;&nbsp;</span>), <br />];
  });
};

type ProviderBAAFormProps = {
  onCheckboxClicked: ChangeEventHandler<HTMLInputElement>;
};

function ProviderBAAForm({ onCheckboxClicked }: ProviderBAAFormProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasScrolledToBottom(true);
        observer.unobserve(bottomRef.current!);
      }
    });

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className={styles.agreementInformation}>
        <div className={styles.icon}>
          <FontAwesomeIcon icon={faLightbulb} title="Information" />
        </div>
        <div className={styles.informationCopy}>
          <p className={styles.informationTitle}>{"What is this agreement"}</p>
          <p>
            {
              "Agreeing to this BAA allows secure, HIPAA-compliant information sharing, ensuring patients can easily file appeals while protecting their privacy."
            }
          </p>
        </div>
      </div>
      <div className={styles.agreementCopy}>
        {createBaaLineElements(BAA_COPY)}
        <div ref={bottomRef} />
      </div>
      <div className={styles.termsAndConditionsContainer}>
        <Checkbox
          label="I have read and agree to the Claimable BAA and service agreement and am authorized to agree on behalf of my organization"
          onChange={onCheckboxClicked}
          disabled={!hasScrolledToBottom}
          classNames={{
            body: styles.checkboxBody,
            inner: styles.checkboxContainer,
            input: styles.checkboxInput,
            icon: styles.checkboxIcon,
          }}
        />
      </div>
    </>
  );
}

export default ProviderBAAForm;
